import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PlatformOrganizationItem, UpdatePlatformOrganizationPayload } from '../../shared/services/api.service';

interface ExistingOrganizationAdmin {
  email: string;
  isBillingOwner: boolean;
}

export interface EditPlatformOrganizationDialogData {
  organization: PlatformOrganizationItem;
  existingAdmins?: ExistingOrganizationAdmin[];
}

@Component({
  selector: 'app-edit-platform-organization-dialog',
  templateUrl: './edit-platform-organization-dialog.component.html',
  styleUrls: ['./edit-platform-organization-dialog.component.css'],
})
export class EditPlatformOrganizationDialogComponent {
  organizationForm: FormGroup;
  emailListError: string | null = null;
  readonly maxAdminEmails = 20;
  readonly existingAdmins: ExistingOrganizationAdmin[];
  selectedBillingOwnerEmail: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<EditPlatformOrganizationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditPlatformOrganizationDialogData
  ) {
    this.existingAdmins = Array.isArray(data.existingAdmins)
      ? data.existingAdmins
          .map((admin) => ({
            email: String(admin.email || '').trim().toLowerCase(),
            isBillingOwner: admin.isBillingOwner === true,
          }))
          .filter((admin) => admin.email.length > 0)
      : [];
    const existingBillingOwner = this.existingAdmins.find((admin) => admin.isBillingOwner);
    this.selectedBillingOwnerEmail = existingBillingOwner ? existingBillingOwner.email : null;

    this.organizationForm = this.fb.group({
      displayName: [data.organization.displayName || '', [Validators.required, Validators.maxLength(120)]],
      primaryColor: [data.organization.primaryColor || '#003366', [Validators.required]],
      secondaryColor: [data.organization.secondaryColor || '#00A3E0', [Validators.required]],
      logoUrl: [data.organization.logoUrl || '', [Validators.required]],
      mailLogoUrl: [data.organization.mailLogoUrl || ''],
      autoApproveNewUsers: [data.organization.autoApproveNewUsers === true],
      billingPaymentExempt: [data.organization.billingPaymentExempt === true],
      billingPaymentType: [data.organization.billingPaymentType || 'CARD', [Validators.required]],
      adminEmails: this.fb.array([this.fb.control('', [Validators.email])]),
    });
  }

  get adminEmails(): FormArray<FormControl<string | null>> {
    return this.organizationForm.get('adminEmails') as FormArray<FormControl<string | null>>;
  }

  addAdminEmailField(): void {
    if (this.adminEmails.length >= this.maxAdminEmails) return;
    this.adminEmails.push(this.fb.control('', [Validators.email]));
  }

  removeAdminEmailField(index: number): void {
    if (this.adminEmails.length <= 1) return;
    const removedEmail = String(this.adminEmails.at(index)?.value || '').trim().toLowerCase();
    this.adminEmails.removeAt(index);
    if (removedEmail && this.selectedBillingOwnerEmail === removedEmail) {
      this.selectedBillingOwnerEmail = this.existingAdmins.find((admin) => admin.isBillingOwner)?.email || null;
    }
  }

  setBillingOwnerEmail(email: string): void {
    this.selectedBillingOwnerEmail = String(email || '').trim().toLowerCase() || null;
  }

  isBillingOwnerEmail(email: string): boolean {
    return String(email || '').trim().toLowerCase() === String(this.selectedBillingOwnerEmail || '').trim().toLowerCase();
  }

  get inviteAdminEmails(): string[] {
    return this.adminEmails.controls
      .map((control) => String(control.value || '').trim().toLowerCase())
      .filter(Boolean);
  }

  get billingOwnerOptions(): string[] {
    return Array.from(new Set([...this.existingAdmins.map((admin) => admin.email), ...this.inviteAdminEmails]));
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (this.organizationForm.invalid) return;
    const raw = this.organizationForm.getRawValue();
    const emails = this.inviteAdminEmails;

    const invalidEmail = emails.find((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmail) {
      this.emailListError = `Invalid email: ${invalidEmail}`;
      return;
    }
    const selectedBillingOwner = String(this.selectedBillingOwnerEmail || '').trim().toLowerCase();
    if (selectedBillingOwner && !this.billingOwnerOptions.includes(selectedBillingOwner)) {
      this.emailListError = 'Billing owner email selection is invalid.';
      return;
    }
    this.emailListError = null;

    const payload: UpdatePlatformOrganizationPayload = {
      displayName: String(raw.displayName || '').trim(),
      primaryColor: String(raw.primaryColor || '').trim(),
      secondaryColor: String(raw.secondaryColor || '').trim(),
      logoUrl: String(raw.logoUrl || '').trim(),
      mailLogoUrl: String(raw.mailLogoUrl || '').trim() || null,
      autoApproveNewUsers: raw.autoApproveNewUsers === true,
      billingPaymentExempt: raw.billingPaymentExempt === true,
      billingPaymentType: String(raw.billingPaymentType || '').trim().toUpperCase() === 'INVOICE' ? 'INVOICE' : 'CARD',
      adminEmails: emails.length > 0 ? emails : undefined,
      billingOwnerEmail: selectedBillingOwner || undefined,
    };
    this.dialogRef.close(payload);
  }
}
